<template>
  <div class="dashboard">
    <div class="dashboard-header">
      <div class="header-content">
        <div class="header-text">
          <h1>Dashboard</h1>
          <p class="subtitle">Overview of your food shop performance</p>
        </div>
        <div class="header-actions">
          <el-button @click="refreshDashboard" :loading="loading" type="info" size="small">
            <el-icon><Refresh /></el-icon>
            Refresh
          </el-button>
          <el-button type="primary" @click="$router.push('/reports/daily')">
            <el-icon><Document /></el-icon>
            Daily Reports
          </el-button>
        </div>
      </div>
    </div>

    <!-- Overview Cards -->
    <div class="overview-cards" v-if="!loading">
      <el-card class="overview-card sales">
        <div class="card-content">
          <div class="card-icon">
            <el-icon><Money /></el-icon>
          </div>
          <div class="card-info">
            <h3>Today's Sales</h3>
            <p class="amount">${{ formatNumber(overview.today.sales) }}</p>
            <span class="label">{{ overview.today.transactionCount }} transactions</span>
          </div>
        </div>
      </el-card>

      <el-card class="overview-card costs">
        <div class="card-content">
          <div class="card-icon">
            <el-icon><ShoppingCart /></el-icon>
          </div>
          <div class="card-info">
            <h3>Today's Costs</h3>
            <p class="amount">${{ formatNumber(overview.today.costs) }}</p>
            <span class="label">Expenses</span>
          </div>
        </div>
      </el-card>

      <el-card class="overview-card profit">
        <div class="card-content">
          <div class="card-icon">
            <el-icon><TrendCharts /></el-icon>
          </div>
          <div class="card-info">
            <h3>Today's Profit</h3>
            <p class="amount" :class="{ 'positive': overview.today.profit >= 0, 'negative': overview.today.profit < 0 }">
              ${{ formatNumber(overview.today.profit) }}
            </p>
            <span class="label">Net income</span>
          </div>
        </div>
      </el-card>

      <el-card class="overview-card monthly">
        <div class="card-content">
          <div class="card-icon">
            <el-icon><Calendar /></el-icon>
          </div>
          <div class="card-info">
            <h3>This Month</h3>
            <p class="amount">${{ formatNumber(overview.thisMonth.profit) }}</p>
            <span class="label">Total profit</span>
          </div>
        </div>
      </el-card>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <el-skeleton :rows="3" animated />
      <el-skeleton :rows="5" animated />
    </div>

    <!-- Charts Section -->
    <div class="charts-section" v-if="!loading && hasData">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>Daily Performance (Current Month)</span>
              </div>
            </template>
            <div class="chart-container">
              <LineChart :data="chartData" :options="chartOptions" />
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="12">
          <el-card class="chart-card">
            <template #header>
              <div class="card-header">
                <span>Sales vs Costs</span>
              </div>
            </template>
            <div class="chart-container">
              <DoughnutChart :data="doughnutData" :options="doughnutOptions" />
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- Recent Transactions -->
    <el-card class="recent-transactions" v-if="!loading">
      <template #header>
        <div class="card-header">
          <span>Recent Transactions</span>
          <el-button type="primary" size="small" @click="$router.push('/transactions')">
            View All
          </el-button>
        </div>
      </template>
      
      <div v-if="overview.recentTransactions.length > 0">
        <div 
          v-for="transaction in overview.recentTransactions" 
          :key="transaction.id"
          class="transaction-item"
        >
          <div class="transaction-info">
            <div class="transaction-type" :class="transaction.type">
              <el-icon v-if="transaction.type === 'sale'"><Money /></el-icon>
              <el-icon v-else><ShoppingCart /></el-icon>
              {{ transaction.type === 'sale' ? 'Sale' : 'Cost' }}
            </div>
            <div class="transaction-details">
              <h4>{{ transaction.description }}</h4>
              <p>{{ transaction.category }} • {{ formatDate(transaction.date) }}</p>
            </div>
          </div>
          <div class="transaction-amount">
            <span :class="{ 'positive': transaction.type === 'sale', 'negative': transaction.type === 'cost' }">
              {{ transaction.type === 'sale' ? '+' : '-' }}${{ formatNumber(transaction.amount) }}
            </span>
          </div>
        </div>
      </div>
      
      <div v-else class="empty-state">
        <el-icon><Document /></el-icon>
        <p>No transactions yet</p>
        <el-button type="primary" @click="$router.push('/transactions/new')">
          Add Your First Transaction
        </el-button>
      </div>
    </el-card>

    <!-- Quick Actions -->
    <el-card class="quick-actions">
      <template #header>
        <div class="card-header">
          <span>Quick Actions</span>
        </div>
      </template>
      
      <div class="actions-grid">
        <el-button type="primary" @click="$router.push('/transactions/new')">
          <el-icon><Plus /></el-icon>
          Add Transaction
        </el-button>
        
        <el-button @click="$router.push('/reports/daily')">
          <el-icon><Document /></el-icon>
          Daily Reports
        </el-button>
        
        <el-button @click="$router.push('/reports/monthly')">
          <el-icon><Calendar /></el-icon>
          Monthly Reports
        </el-button>
        
        <el-button @click="$router.push('/analytics')">
          <el-icon><TrendCharts /></el-icon>
          Analytics
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import { Money, ShoppingCart, TrendCharts, Calendar, Plus, Document, Refresh } from '@element-plus/icons-vue'
import { Line as LineChart, Doughnut as DoughnutChart } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement)

export default {
  name: 'Dashboard',
  components: {
    LineChart,
    DoughnutChart,
    Money,
    ShoppingCart,
    TrendCharts,
    Calendar,
    Plus,
    Document,
    Refresh
  },
  setup() {
    const store = useStore()
    const loading = ref(true)
    const dailyTotals = ref([])

    const overview = computed(() => store.getters['dashboard/overview'])
    const hasData = computed(() => store.getters['dashboard/hasData'])

    const formatNumber = (num) => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)
    }

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // Chart data for daily performance - Show current month by day
    const chartData = computed(() => {
      console.log('🔄 Computing chart data with dailyTotals:', dailyTotals.value)
      
      // Get all days in the current month
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
      
      const days = []
      const salesData = []
      const costsData = []
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(currentYear, currentMonth, day)
        const dateStr = date.toISOString().split('T')[0]
        
        // Format day label as "Aug 1", "Aug 2", etc.
        const dayLabel = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        })
        days.push(dayLabel)
        
        // Find data for this day from daily totals
        const dayData = dailyTotals.value && dailyTotals.value.find(d => d._id === dateStr)
        
        if (dayData) {
          console.log(`✅ Found data for ${dateStr}:`, dayData)
          salesData.push(dayData.totalSales || 0)
          costsData.push(dayData.totalCosts || 0)
        } else {
          console.log(`❌ No data found for ${dateStr}`)
          salesData.push(0)
          costsData.push(0)
        }
      }
      
      const chartData = {
        labels: days,
        datasets: [
          {
            label: 'Sales',
            data: salesData,
            borderColor: '#67c23a',
            backgroundColor: 'rgba(103, 194, 58, 0.1)',
            tension: 0.4
          },
          {
            label: 'Costs',
            data: costsData,
            borderColor: '#f56c6c',
            backgroundColor: 'rgba(245, 108, 108, 0.1)',
            tension: 0.4
          }
        ]
      }
      
      console.log('📊 Final chart data:', chartData)
      return chartData
    })

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top'
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }

    // Doughnut chart data
    const doughnutData = computed(() => ({
      labels: ['Sales', 'Costs'],
      datasets: [{
        data: [overview.value.thisMonth.sales, overview.value.thisMonth.costs],
        backgroundColor: ['#67c23a', '#f56c6c'],
        borderWidth: 0
      }]
    }))

    const doughnutOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      }
    }

    const loadDailyTotals = async () => {
      try {
        const currentDate = new Date()
        const currentYear = currentDate.getFullYear()
        const currentMonth = currentDate.getMonth()
        
        const startDate = new Date(currentYear, currentMonth, 1).toISOString().split('T')[0]
        const endDate = new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0]
        
        console.log('📊 Loading daily totals for current month:', { startDate, endDate })
        
        // Use debounced version to prevent rapid successive calls
        await store.dispatch('dashboard/fetchDailyTotalsDebounced', {
          startDate,
          endDate
        })
        
        const totals = store.getters['dashboard/dailyTotals']
        console.log('📈 Daily totals from store:', totals)
        dailyTotals.value = totals || []
        console.log('📈 Daily totals set in component:', dailyTotals.value)
        
        // Debug: Check if we have any transactions in the date range
        if (dailyTotals.value.length === 0) {
          console.log('⚠️ No daily totals found. Checking if there are any transactions...')
          // Let's also check the overview to see if there are any transactions
          const overview = store.getters['dashboard/overview']
          console.log('📋 Overview data:', overview)
        }
      } catch (error) {
        console.error('❌ Failed to load daily totals for chart:', error)
        
        // Handle authentication errors (no auth required)
        if (error.response?.status === 401) {
          console.log('🚫 Authentication error - no auth required, continuing')
          return
        }
        
        // Handle rate limiting specifically
        if (error.response?.status === 429) {
          console.log('⏰ Rate limited, will retry automatically')
          // The store will handle retry with exponential backoff
          return
        }
        
        dailyTotals.value = []
      }
    }

    const testDailyTotals = async () => {
      try {
        console.log('🧪 Testing daily totals API...')
        
        // Test with a wider date range
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - 30) // Last 30 days
        
        const startDate = start.toISOString().split('T')[0]
        const endDate = end.toISOString().split('T')[0]
        
        console.log('🧪 Testing with date range:', { startDate, endDate })
        
        // Use axios instead of fetch to respect the configured baseURL
        const response = await axios.get(`/dashboard/daily-totals?startDate=${startDate}&endDate=${endDate}`)
        
        if (response.status === 200) {
          const data = response.data
          console.log('🧪 API Response:', data)
        } else {
          console.error('🧪 API Error:', response.status, response.statusText)
        }
      } catch (error) {
        console.error('🧪 Test failed:', error)
      }
    }

    const loadDashboard = async () => {
      try {
        loading.value = true
        
        // Load overview first, then daily totals sequentially to avoid overwhelming the API
        console.log('📊 Loading dashboard data sequentially...')
        
        // Load overview first
        await store.dispatch('dashboard/fetchOverview')
        console.log('✅ Overview loaded')
        
        // Small delay to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Then load daily totals
        await loadDailyTotals()
        console.log('✅ Daily totals loaded')
        
      } catch (error) {
        console.error('Failed to load dashboard:', error)
        
        // Handle rate limiting
        if (error.response?.status === 429) {
          console.log('⏰ Rate limited during dashboard load, will retry automatically')
          // Show user-friendly message
          // You could add a toast notification here
        }
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadDashboard()
    })

    onUnmounted(() => {
      // Clear any pending debounce timers
      if (store.state.dashboard.cache) {
        console.log('🧹 Clearing dashboard cache on unmount')
        store.commit('dashboard/CLEAR_CACHE')
      }
    })

    // Add a refresh function that can be called manually
    const refreshDashboard = async () => {
      console.log('🔄 Manually refreshing dashboard...')
      // Clear cache first
      store.commit('dashboard/CLEAR_CACHE')
      // Reload data
      await loadDashboard()
    }

    return {
      loading,
      overview,
      hasData,
      dailyTotals,
      formatNumber,
      formatDate,
      chartData,
      chartOptions,
      doughnutData,
      doughnutOptions,
      testDailyTotals,
      refreshDashboard
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 20px;
}

.dashboard-header {
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.header-text {
  flex: 1;
}

.dashboard-header h1 {
  font-size: 2rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
}

.subtitle {
  color: #7f8c8d;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .header-actions {
    justify-content: center;
  }
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.overview-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.overview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.card-content {
  display: flex;
  align-items: center;
  gap: 15px;
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.sales .card-icon {
  background: linear-gradient(135deg, #67c23a, #85ce61);
  color: white;
}

.costs .card-icon {
  background: linear-gradient(135deg, #f56c6c, #f78989);
  color: white;
}

.profit .card-icon {
  background: linear-gradient(135deg, #409eff, #66b1ff);
  color: white;
}

.monthly .card-icon {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
  color: white;
}

.card-info h3 {
  font-size: 0.9rem;
  font-weight: 500;
  color: #7f8c8d;
  margin: 0 0 5px 0;
}

.amount {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 5px 0;
}

.amount.positive {
  color: #67c23a;
}

.amount.negative {
  color: #f56c6c;
}

.label {
  font-size: 0.8rem;
  color: #95a5a6;
}

.charts-section {
  margin-bottom: 30px;
}

.chart-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.chart-container {
  height: 300px;
  position: relative;
}

.recent-transactions {
  margin-bottom: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.transaction-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.transaction-item:last-child {
  border-bottom: none;
}

.transaction-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.transaction-type {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.transaction-type.sale {
  background: rgba(103, 194, 58, 0.1);
  color: #67c23a;
}

.transaction-type.cost {
  background: rgba(245, 108, 108, 0.1);
  color: #f56c6c;
}

.transaction-details h4 {
  font-size: 0.95rem;
  font-weight: 500;
  color: #2c3e50;
  margin: 0 0 2px 0;
}

.transaction-details p {
  font-size: 0.8rem;
  color: #7f8c8d;
  margin: 0;
}

.transaction-amount {
  font-weight: 600;
  font-size: 1rem;
}

.transaction-amount .positive {
  color: #67c23a;
}

.transaction-amount .negative {
  color: #f56c6c;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #7f8c8d;
}

.empty-state .el-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  color: #c0c4cc;
}

.empty-state p {
  margin: 0 0 20px 0;
  font-size: 1rem;
}

.quick-actions {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.loading-state {
  margin-bottom: 30px;
}

/* Responsive design */
@media (max-width: 768px) {
  .overview-cards {
    grid-template-columns: 1fr;
  }
  
  .actions-grid {
    grid-template-columns: 1fr;
  }
  
  .chart-container {
    height: 250px;
  }
}
</style> 